import React from 'react';

/**
 * Page section wrapper.
 *
 * Holds the layout rhythm from the design reference
 * (docs/revamp-july-2026/handoff/Focal Point Site.dc.html): a 1360px content
 * column inside a full-bleed outer element, so a band's background and its
 * hairline borders reach the viewport edges while the copy stays in the column.
 *
 * Gutters step down from 72px to 24px below the md breakpoint. The reference is
 * desktop-only, so every responsive value here is derived rather than lifted.
 */

interface SectionProps {
  id?: string;
  /**
   * 'plain' sits on the page background.
   * 'band' sits on --sand-2 with a hairline top and bottom border, which is how
   * the reference alternates sections.
   */
  variant?: 'plain' | 'band';
  /**
   * Vertical rhythm.
   * 'default' is 76px top / 20px bottom, the reference's standard section.
   * 'band' is an even 64px, used inside --sand-2 bands.
   * 'tight' is 34px, used by the footer and the client logo strip.
   */
  spacing?: 'default' | 'band' | 'tight';
  /** Applied to the inner 1360px column, not the full-bleed outer element. */
  className?: string;
  children?: React.ReactNode;
}

const SPACING: Record<NonNullable<SectionProps['spacing']>, string> = {
  default: 'pt-12 pb-5 md:pt-[76px] md:pb-5',
  band: 'py-10 md:py-16',
  tight: 'py-6 md:py-[34px]',
};

export default function Section({
  id,
  variant = 'plain',
  spacing = 'default',
  className = '',
  children,
}: SectionProps) {
  const isBand = variant === 'band';

  return (
    <section id={id} className={isBand ? 'bg-sand-2 border-y border-line' : undefined}>
      <div
        className={`mx-auto max-w-[1360px] px-6 md:px-[72px] ${SPACING[spacing]} ${className}`.trim()}
      >
        {children}
      </div>
    </section>
  );
}
